import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { ModalComponent } from '@shared/components/modal/modal.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import {
  ConfirmDialogService,
  ConfirmDialogIcon,
  ConfirmDialogVariant
} from './confirm-dialog.service';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule, ModalComponent, ButtonComponent],
  styles: [`
    .confirm-shell {
      display: flex;
      align-items: flex-start;
      gap: 0.875rem;
      text-align: left;
    }

    .confirm-shell.is-stacked {
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .confirm-icon {
      width: 2.75rem;
      height: 2.75rem;
      border-radius: 0.85rem;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .confirm-shell.is-stacked .confirm-icon {
      width: 3.25rem;
      height: 3.25rem;
      border-radius: 999px;
      margin-bottom: 0.15rem;
    }

    .confirm-icon svg {
      width: 1.35rem;
      height: 1.35rem;
    }

    .confirm-shell.is-stacked .confirm-icon svg {
      width: 1.5rem;
      height: 1.5rem;
    }

    .confirm-icon.is-danger {
      background: #fef2f2;
      color: #dc2626;
      border: 1px solid rgba(254, 202, 202, 0.9);
    }

    .confirm-icon.is-info {
      background: #eff6ff;
      color: #2563eb;
      border: 1px solid rgba(191, 219, 254, 0.9);
    }

    .confirm-icon.is-sign-out {
      background: linear-gradient(160deg, #f8fafc 0%, #eef2ff 100%);
      color: #334155;
      border: 1px solid #e2e8f0;
    }

    .confirm-copy {
      min-width: 0;
      flex: 1;
    }

    .confirm-message {
      margin: 0;
      font-size: 0.875rem;
      line-height: 1.55;
      color: #475569;
    }

    .confirm-shell.is-stacked .confirm-message {
      max-width: 20rem;
    }

    .confirm-foot {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.65rem;
      width: 100%;
    }
  `],
  template: `
    <app-modal
      [isOpen]="isOpen"
      [heading]="title"
      [subtitle]="subtitle"
      size="sm"
      [showCloseButton]="true"
      [closeOnBackdrop]="true"
      [hasFooter]="true"
      (closed)="onCancel()">
      <div
        class="confirm-shell"
        [class.is-stacked]="resolvedIcon === 'warning'">
        <div
          class="confirm-icon"
          [class.is-danger]="resolvedIcon === 'warning'"
          [class.is-info]="resolvedIcon === 'info'"
          [class.is-sign-out]="resolvedIcon === 'sign-out'"
          aria-hidden="true">
          <svg *ngIf="resolvedIcon === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <svg *ngIf="resolvedIcon === 'info'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <svg *ngIf="resolvedIcon === 'sign-out'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </div>
        <div class="confirm-copy">
          <p class="confirm-message">{{ message }}</p>
        </div>
      </div>

      <div modal-footer class="confirm-foot">
        <app-button variant="ghost" (buttonClick)="onCancel()">{{ cancelLabel }}</app-button>
        <app-button
          [variant]="confirmButtonVariant"
          (buttonClick)="onConfirm()">
          {{ confirmLabel }}
        </app-button>
      </div>
    </app-modal>
  `
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
  private confirmDialogService = inject(ConfirmDialogService);
  private destroy$ = new Subject<void>();
  private resolve?: (confirmed: boolean) => void;

  isOpen = false;
  title = '';
  subtitle = '';
  message = '';
  confirmLabel = 'Confirm';
  cancelLabel = 'Cancel';
  variant: ConfirmDialogVariant = 'default';
  icon: ConfirmDialogIcon = 'auto';

  get resolvedIcon(): 'warning' | 'info' | 'sign-out' {
    if (this.icon === 'sign-out') {
      return 'sign-out';
    }
    if (this.icon === 'warning') {
      return 'warning';
    }
    if (this.icon === 'info') {
      return 'info';
    }
    return this.variant === 'danger' ? 'warning' : 'info';
  }

  get confirmButtonVariant(): 'primary' | 'danger' {
    if (this.resolvedIcon === 'sign-out') {
      return 'primary';
    }
    return this.variant === 'danger' ? 'danger' : 'primary';
  }

  ngOnInit(): void {
    this.confirmDialogService.request$
      .pipe(takeUntil(this.destroy$))
      .subscribe(request => {
        if (!request) {
          this.close(false);
          return;
        }

        this.title = request.options.title;
        this.subtitle = request.options.subtitle;
        this.message = request.options.message;
        this.confirmLabel = request.options.confirmLabel;
        this.cancelLabel = request.options.cancelLabel;
        this.variant = request.options.variant;
        this.icon = request.options.icon;
        this.resolve = request.resolve;
        this.isOpen = true;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onConfirm(): void {
    this.close(true);
  }

  onCancel(): void {
    this.close(false);
  }

  private close(confirmed: boolean): void {
    if (!this.isOpen && !this.resolve) {
      return;
    }
    this.isOpen = false;
    const resolve = this.resolve;
    this.resolve = undefined;
    resolve?.(confirmed);
  }
}
